import { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { createSuccessSnackbar } from 'components/Snackbar';
import useOrderNotificationPrefs from 'hooks/useOrderNotificationPrefs';
import { selectPageIsVisible } from 'selectors/ui';

import messages from './SkuOrderDashboard.messages';
import { claimUnnotifiedOrderIds, getNewOrderIds, getOrderIds } from './SkuOrderNotifications.helpers';
import { playNewOrderSound } from './SkuOrderNotifications.sound';

const TITLE_BADGE_PATTERN = /^\(\d+\) /;
const OS_NOTIFICATION_TAG = 'cp-new-orders';

export const PERMISSION_PROMPTED_STORAGE_KEY = 'cp-order-notifications-permission-prompted';

export function setTitleBadge(count) {
  if (typeof document === 'undefined') return;

  const baseTitle = document.title.replace(TITLE_BADGE_PATTERN, '');
  document.title = count > 0 ? `(${count}) ${baseTitle}` : baseTitle;
}

function showOsNotification(title, options) {
  try {
    // tag makes newer notifications replace older ones instead of stacking, including across tabs.
    return new Notification(title, { ...options, tag: OS_NOTIFICATION_TAG });
  } catch (_error) {
    return null;
  }
}

function browserNotificationsSupported() {
  return typeof Notification !== 'undefined' && typeof Notification.requestPermission === 'function';
}

export function useNewOrderNotifications({ orders, dataUpdatedAt, detectionKey, enabled }) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const pageIsVisible = useSelector(selectPageIsVisible);
  const { prefs } = useOrderNotificationPrefs();

  const seenOrderIdsRef = useRef(null);
  const lastDetectionKeyRef = useRef(null);
  const badgeCountRef = useRef(0);

  useEffect(() => {
    if (pageIsVisible && badgeCountRef.current > 0) {
      badgeCountRef.current = 0;
      setTitleBadge(0);
    }
  }, [pageIsVisible]);

  useEffect(
    () => () => {
      if (badgeCountRef.current > 0) setTitleBadge(0);
    },
    []
  );

  useEffect(() => {
    if (!enabled || !prefs.browserNotificationsEnabled || !browserNotificationsSupported()) return;
    if (Notification.permission !== 'default') return;

    try {
      // Ask only once from the Orders page; afterwards the account notification settings own the prompt.
      if (window.localStorage.getItem(PERMISSION_PROMPTED_STORAGE_KEY)) return;
      window.localStorage.setItem(PERMISSION_PROMPTED_STORAGE_KEY, '1');
    } catch (_error) {
      return;
    }

    // Safari only grants requestPermission from a user gesture and ignores this call;
    // there the checkbox on the notification settings page is the way to grant it.
    const request = Notification.requestPermission();
    if (request?.catch) {
      request.catch(() => {});
    }
  }, [enabled, prefs.browserNotificationsEnabled]);

  useEffect(() => {
    if (!enabled || !dataUpdatedAt) return;

    const isFirstLoad = seenOrderIdsRef.current === null;
    const detectionKeyChanged = lastDetectionKeyRef.current !== detectionKey;

    if (isFirstLoad || detectionKeyChanged) {
      lastDetectionKeyRef.current = detectionKey;
      seenOrderIdsRef.current = getOrderIds(orders);
      return;
    }

    const newOrderIds = getNewOrderIds(seenOrderIdsRef.current, orders);
    seenOrderIdsRef.current = getOrderIds(orders);
    if (!newOrderIds.length) return;

    const unnotifiedOrderIds = claimUnnotifiedOrderIds(newOrderIds);
    if (!unnotifiedOrderIds.length) return;

    const count = unnotifiedOrderIds.length;
    dispatch(createSuccessSnackbar(messages.newOrdersReceived, { count }));

    const tabIsHidden = typeof document !== 'undefined' && document.hidden;
    if (tabIsHidden) {
      badgeCountRef.current += count;
      setTitleBadge(badgeCountRef.current);
    }

    if (prefs.soundEnabled) {
      playNewOrderSound();
    }

    const canShowOsNotification =
      tabIsHidden &&
      prefs.browserNotificationsEnabled &&
      browserNotificationsSupported() &&
      Notification.permission === 'granted';

    if (canShowOsNotification) {
      // silent mirrors the sound pref so the chime and the OS notification sound never both play.
      showOsNotification(intl.formatMessage(messages.newOrdersReceived, { count }), { silent: prefs.soundEnabled });
    }
  }, [enabled, dataUpdatedAt, detectionKey, orders, dispatch, intl, prefs]);
}
